/**
 * Installation Product Handler - Integrated Version
 * Manages the installation product add-on functionality
 * Works with existing product-form.js and complementary-products.js
 */

class InstallationProductManager {
  constructor() {
    this.container = document.querySelector('.installation-product-container');
    if (!this.container) return;

    this.installationProductId = this.container.dataset.installationProductId;
    this.allowedLocation = this.container.dataset.allowedLocation;
    this.isSelected = false;
    this.isMetroArea = false;
    this.mainProductId = null;
    
    this.init();
  }

  init() {
    this.getMainProductId();
    this.setupMetroToggle();
    this.setupInstallationToggle();
    this.integrateWithExistingButtons();
    this.preventInstallationQtyChange();
  }

  getMainProductId() {
    // Get the main product ID from the page
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      this.mainProductId = productInfo.dataset.productId;
    }
  }

  setupMetroToggle() {
    const metroToggle = this.container.querySelector('.metro-area-toggle-input');
    const installationSection = this.container.querySelector('.installation-toggle-section');

    if (metroToggle && installationSection) {
      metroToggle.addEventListener('change', (e) => {
        this.isMetroArea = e.target.checked;
        
        if (this.isMetroArea) {
          // Show installation section with animation
          installationSection.style.display = 'block';
        } else {
          // Hide installation section
          installationSection.style.display = 'none';
          
          // Reset installation selection
          const noRadio = this.container.querySelector('.installation-no');
          if (noRadio) {
            noRadio.checked = true;
            this.isSelected = false;
            this.container.classList.remove('installation-selected');
          }
        }
      });
    }
  }

  setupInstallationToggle() {
    const yesRadio = this.container.querySelector('.installation-yes');
    const noRadio = this.container.querySelector('.installation-no');
    const priceDisplay = this.container.querySelector('.installation-price-display');

    if (yesRadio) {
      yesRadio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.isSelected = true;
          this.container.classList.add('installation-selected');
          // Show price when Yes is selected
          if (priceDisplay) {
            priceDisplay.style.display = 'block';
          }
        }
      });
    }

    if (noRadio) {
      noRadio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.isSelected = false;
          this.container.classList.remove('installation-selected');
          // Hide price when No is selected
          if (priceDisplay) {
            priceDisplay.style.display = 'none';
          }
        }
      });
    }
  }

  integrateWithExistingButtons() {
    // Hook into all submit buttons to add installation product data
    document.addEventListener('click', (e) => {
      const submitButton = e.target.closest('button[type="submit"]');
      if (!submitButton) return;

      // Check if it's an ATC button
      const isATCButton = submitButton.hasAttribute('data-sold-out-message') || 
                         submitButton.classList.contains('widget-atc-button') ||
                         submitButton.classList.contains('sticky-atc') ||
                         submitButton.classList.contains('card-bundler-atc-button');

      if (!isATCButton) return;

      // IMPORTANT: Check if this button is for the main product or a YML/cart product
      const isMainProductButton = this.isMainProductButton(submitButton);
      
      // Only add installation if:
      // 1. It's selected
      // 2. Metro area is enabled
      // 3. It's the main product button (NOT a YML product)
      if (isMainProductButton && this.isSelected && this.isMetroArea) {
        submitButton.dataset.installationProductId = this.installationProductId;
      } else {
        // Remove installation data for all other cases
        delete submitButton.dataset.installationProductId;
      }
    }, true);
  }

  isMainProductButton(button) {
    // Check if button is in cart drawer or YML section
    const isInCartDrawer = button.closest('cart-drawer');
    const isInYMLSection = button.closest('.cart-up-rec') || 
                          button.closest('.cart-upsell-recommendations') ||
                          button.closest('.small-product-card');
    
    // If in cart drawer or YML section, it's NOT the main product button
    if (isInCartDrawer || isInYMLSection) {
      return false;
    }

    // Check if button is in the main product form
    const productForm = button.closest('product-form');
    if (productForm) {
      const productInfo = productForm.closest('product-info');
      if (productInfo && productInfo.dataset.productId === this.mainProductId) {
        return true;
      }
    }

    // Check if it's in the main product section
    const mainProductSection = button.closest('#MainContent');
    if (mainProductSection) {
      const inQuickAdd = button.closest('quick-add-modal');
      // If it's in a quick-add modal within main content, it's NOT main product
      if (inQuickAdd) return false;
      
      return true;
    }

    return false;
  }

  preventInstallationQtyChange() {
    // Listen for cart updates to sync installation quantity with main product
    document.addEventListener('cart:updated', () => {
      this.syncInstallationQuantity();
    });

    // Also observe cart DOM changes
    const observer = new MutationObserver(() => {
      this.syncInstallationQuantity();
    });

    const cartElement = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (cartElement) {
      observer.observe(cartElement, {
        childList: true,
        subtree: true
      });
    }
  }

  async syncInstallationQuantity() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Find all installation items
      const installationItems = cart.items.filter(item => 
        item.properties && item.properties._is_installation === 'true'
      );

      // Sync each installation item with its parent
      for (const installItem of installationItems) {
        const parentVariantId = installItem.properties._installation_for;
        
        // Find parent product
        const parentItem = cart.items.find(item => 
          item.variant_id == parentVariantId &&
          item.properties && 
          item.properties._installation_parent === 'true'
        );

        if (parentItem && installItem.quantity !== parentItem.quantity) {
          // Update installation quantity to match parent
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: installItem.key,
              quantity: parentItem.quantity
            })
          });
        }
      }
    } catch (error) {
      console.error('Error syncing installation quantity:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InstallationProductManager();
  });
} else {
  new InstallationProductManager();
}